import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  maxWidth?: string;
  multiline?: boolean;
}

export const Tooltip = ({ 
  children, 
  content, 
  position = 'top',
  delay = 300,
  maxWidth = 'max-w-xs',
  multiline = true
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(position);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    const id = setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  // Handle touch for mobile devices
  const handleTouchStart = () => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    
    // Show tooltip immediately on touch for better mobile UX
    const id = setTimeout(() => setIsVisible(true), 100);
    setTimeoutId(id);
  };

  const handleTouchEnd = () => {
    // Hide tooltip after a delay on touch end
    const id = setTimeout(() => {
      setIsVisible(false);
    }, 1500);
    setTimeoutId(id);
  };

  const responsivePositionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };

  // Adjust tooltip position on small screens
  useEffect(() => {
    if (!isVisible || !tooltipRef.current || !triggerRef.current) return;

    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get bounding rectangles
    const tooltipRect = tooltip.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    
    // Check if tooltip would overflow viewport
    let newPosition = position;
    
    if (position === 'top' && triggerRect.top - tooltipRect.height < 10) {
      newPosition = 'bottom';
    } else if (position === 'bottom' && triggerRect.bottom + tooltipRect.height > viewportHeight - 10) {
      newPosition = 'top';
    } else if (position === 'left' && triggerRect.left - tooltipRect.width < 10) {
      newPosition = 'right';
    } else if (position === 'right' && triggerRect.right + tooltipRect.width > viewportWidth - 10) {
      newPosition = 'left';
    }
    
    const frame = window.requestAnimationFrame(() => {
      setTooltipPosition(newPosition);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [isVisible, position]);

  return (
    <div 
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={hideTooltip}
    >
      {children}
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg shadow-lg animate-fade-in ${maxWidth} ${
            multiline ? 'whitespace-normal break-words' : 'whitespace-nowrap'
          } ${responsivePositionClasses[tooltipPosition]}`}
          role="tooltip"
          style={{
            maxWidth: 'min(calc(100vw - 2rem), 24rem)', // Responsive max width
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          <div className="relative z-10">
            {content}
          </div>
          <div className={`absolute w-2 h-2 bg-slate-900 transform rotate-45 ${
            tooltipPosition === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1' :
            tooltipPosition === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1' :
            tooltipPosition === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1' :
            'right-full top-1/2 -translate-y-1/2 -mr-1'
          }`} />
        </div>
      )}
    </div>
  );
};